/*
 * Swiftype search
 * Shared by the "Swift Search" section (sections/swift-search.liquid) and the search drawer
 * (sections/search-drawer.liquid). Each `.st-search` widget on the page is
 * initialised independently, so multiple instances can coexist without the
 * global-scope / duplicate-id clashes the old inline script suffered from.
 */
(function () {
  const PER_PAGE = 10;
  const SUGGEST_ALLOWED = [
    '/products/',
    '/pages/customer-centre-hub/',
    '/pages/size-chart',
    '/pages/customer-centre-article/',
    '/collections/',
  ];

  function escapeHtml(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Only allow http(s) and site-relative URLs to reach an href/src attribute,
  // so a crafted result can't inject javascript: (or similar) schemes.
  function safeUrl(url) {
    const value = String(url == null ? '' : url).trim();
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value;
    return '#';
  }

  function stripHtml(html) {
    return html.replace(/<[^>]*>/g, '');
  }

  function normalizeDomain(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '');
  }

  // Exact hostname match (www. ignored) so "merrypeople.com" doesn't also
  // match "uk.merrypeople.com".
  function matchesDomain(url, domain) {
    if (!domain) return true;
    try {
      return new URL(url).hostname.toLowerCase().replace(/^www\./, '') === domain;
    } catch (e) {
      return false;
    }
  }

  // The domain filter is a theme setting exposed on the search drawer, which
  // is rendered on every page — read it from there so all widget instances
  // (drawer and Swift Search sections) apply it.
  function getDomainFilter() {
    const source = document.querySelector('.st-search[data-domain-filter]');
    return normalizeDomain(source ? source.dataset.domainFilter : '');
  }

  const SNIPPET_LENGTH = 110;

  function decodeEntities(text) {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }

  function toPlainText(html) {
    return decodeEntities(stripHtml(html)).replace(/\s+/g, ' ').trim();
  }

  // Prefer the page's description (the swiftype description meta tag in
  // theme.liquid). Otherwise use Swiftype's body highlight, the passage around
  // the query. Fragments can open mid-word, so drop a leading partial word
  // (checked against the full body) and stray punctuation.
  function getSnippet(item) {
    const description = toPlainText(item.description || '');
    if (description) return truncateWords(description, SNIPPET_LENGTH);

    const body = toPlainText(item.body || '');
    let text = toPlainText((item.highlight && item.highlight.body) || '');
    if (!text) return truncateWords(body, SNIPPET_LENGTH);

    const at = body.indexOf(text);
    if (at > 0 && /\w/.test(body[at - 1])) text = text.replace(/^\S*\s*/, '');
    text = text.replace(/^[^\w"'(]+/, '');
    return truncateWords(text, SNIPPET_LENGTH);
  }

  function truncateWords(text, length) {
    if (text.length <= length) return text;
    const cut = text.slice(0, length + 1);
    const end = cut.lastIndexOf(' ');
    return (end > 0 ? cut.slice(0, end) : text.slice(0, length)).replace(/[\s,;:.\-]+$/, '') + '…';
  }

  function getPrice(body) {
    const match = body.match(/Sale price\$?([\d,]+\.?\d*)\s*([A-Z]{3})/);
    return match ? `$${match[1]} ${match[2]}` : null;
  }

  function categorizeResults(results) {
    const cats = {};
    results.forEach((item) => {
      let category;
      if (item.url.includes('/pages/customer-centre-article/') || item.url.includes('/pages/customer-centre-hub/')) {
        category = 'Help';
      } else if (item.url.includes('/blogs/')) {
        category = 'Blog Posts';
      } else if (item.url.includes('/pages/size')) {
        category = 'Size Charts';
      } else if (item.url.includes('/collections/')) {
        category = 'Collections';
      } else if (item.url.includes('/products/')) {
        category = 'Products';
      } else if (item.url.includes('/pages/')) {
        category = 'Pages';
      } else {
        category = 'Other';
      }
      if (!cats[category]) cats[category] = [];
      cats[category].push(item);
    });

    return Object.entries(cats)
      .sort((a, b) => {
        if (a[0] === 'Products') return -1;
        if (b[0] === 'Products') return 1;
        if (a[0] === 'Help') return -1;
        if (b[0] === 'Help') return 1;
        if (a[0] === 'Size Charts') return -1;
        if (b[0] === 'Size Charts') return 1;
        return b[1].length - a[1].length;
      })
      .map(([name, items]) => ({ name, items }));
  }

  function renderTabs(categories, activeCategory) {
    if (categories.length <= 1) return '';
    return `
      <div class="st-tabs">
        <button class="st-tab ${activeCategory === 'All' ? 'st-tab--active' : ''}" data-category="All">
          All
        </button>
        ${categories
          .map(
            (cat) => `
          <button
            class="st-tab ${cat.name === activeCategory ? 'st-tab--active' : ''}"
            data-category="${cat.name}">
            ${escapeHtml(cat.name)}
          </button>
        `
          )
          .join('')}
      </div>
    `;
  }

  function initSearch(root) {
    const ENGN = root.dataset.engineKey || '';
    const input = root.querySelector('#st-search-input');
    const container = root.querySelector('#st-results-container');
    const suggestions = root.querySelector('#st-suggestions');
    const searchBtn = root.querySelector('#st-search-button');
    const closeBtn = root.querySelector('#st-search-close');

    if (!input || !container || !suggestions || !searchBtn) return;

    let EXTERNAL_URL = container.dataset.externalUrl || '';
    // If the external link points at the page we're already on, behave as a
    // local instance — the same section config can then power both the
    // redirecting widgets and the destination page without self-redirecting.
    if (EXTERNAL_URL) {
      try {
        const target = new URL(EXTERNAL_URL, window.location.origin);
        if (target.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '')) {
          EXTERNAL_URL = '';
        }
      } catch (e) {}
    }
    const DOMAIN_FILTER = getDomainFilter();
    // Server-side filter on the domain-identifier meta tag rendered in
    // theme.liquid, so other-domain results don't consume per_page slots.
    // Only effective once Swiftype has recrawled pages carrying the tag.
    const DOMAIN_FILTER_PARAM = DOMAIN_FILTER
      ? `&filters[page][domain-identifier]=${encodeURIComponent(DOMAIN_FILTER)}`
      : '';
    const filterMode = container.dataset.filterMode || 'some';
    const raw = (container.dataset.filter || '').trim();

    let FILTER_URLS = raw === '' ? [] : raw.split(',').map((f) => f.trim());

    if (window.location.pathname.includes('customer-centre')) {
      FILTER_URLS = ['customer-centre-article', 'customer-centre-hub'];
    }

    let currentPage = 1;
    let currentQuery = '';
    let allResults = [];
    let categories = [];
    let currentCategory = 'All';
    let suggestTimer;

    function highlightQuery(query) {
      if (!query) return;
      const terms = query.trim().split(/\s+/).filter(Boolean);
      const els = container.querySelectorAll('.st-result-link, .st-result-body');
      els.forEach((el) => {
        let html = el.innerHTML;
        terms.forEach((term) => {
          const regex = new RegExp(`(${term})`, 'gi');
          html = html.replace(regex, '<mark>$1</mark>');
        });
        el.innerHTML = html;
      });
    }

    function renderPage(results, page) {
      const start = (page - 1) * PER_PAGE;
      const end = start + PER_PAGE;
      const pageResults = results.slice(start, end);
      const totalPages = Math.ceil(results.length / PER_PAGE);

      container.style.minHeight = container.offsetHeight + 'px';

      if (pageResults.length === 0) {
        container.innerHTML = renderTabs(categories, currentCategory) + '<p>No results found.</p>';
        return;
      }

      const cards = pageResults
        .map((item) => {
          const isProduct = item.url.includes('/products/');
          const isCollection = item.url.includes('/collections/');

          let bottomText = '';
          if (isProduct) {
            const price = getPrice(item.body || '');
            bottomText = price
              ? `<p class="st-result-price" style="margin:unset !important">${escapeHtml(price)}</p>`
              : '';
          } else if (!isCollection) {
            const snippet = getSnippet(item);
            bottomText = snippet
              ? `<p class="st-result-body" style="margin:unset !important; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;">${escapeHtml(snippet)}</p>`
              : '';
          }

          return `
        <div class="st-result">
          <a href="${escapeHtml(safeUrl(item.url))}" style="text-decoration: none !important; padding-bottom: 20px;">
            <div class="h-stack gap-4">
              <img src="${escapeHtml(safeUrl(item.image || ''))}" alt="${escapeHtml(
            item.title
          )}" style="width: 96px; height: 96px; object-fit: cover; border-radius: 2px; flex-shrink: 0; margin-bottom: 6px;" />
              <div class="v-stack" ${
                bottomText ? 'style="margin:unset;"' : 'style="margin:unset; align-self: baseline;"'
              }>
                <span class="st-result-link">${escapeHtml(item.title)}</span>
                ${bottomText}
              </div>
            </div>
          </a>
        </div>
      `;
        })
        .join('');

      const pagination =
        totalPages > 1
          ? `
      <div class="st-pagination">
        <button data-page="${page - 1}" ${page <= 1 ? 'style="visibility: hidden;"' : ''}>Previous</button>
        <span>Page ${page} of ${totalPages}</span>
        <button data-page="${page + 1}" ${page >= totalPages ? 'style="visibility: hidden;"' : ''}>Next</button>
      </div>
    `
          : '';

      container.innerHTML = renderTabs(categories, currentCategory) + cards + pagination;
    }

    function renderResults() {
      const filtered =
        currentCategory === 'All'
          ? allResults
          : categories.find((c) => c.name === currentCategory)?.items || allResults;
      renderPage(filtered, currentPage);
      highlightQuery(currentQuery);
    }

    function goToPage(page) {
      currentPage = page;
      renderResults();
      input.scrollIntoView({ behavior: 'smooth' });
    }

    function hideSuggestions() {
      suggestions.style.display = 'none !important';
      suggestions.innerHTML = '';
    }

    function showSuggestions(query) {
      fetch(
        `https://api.swiftype.com/api/v1/public/engines/suggest.json?engine_key=${ENGN}&q=${encodeURIComponent(
          query
        )}&per_page=5${DOMAIN_FILTER_PARAM}`
      )
        .then((r) => r.json())
        .then((data) => {
          const items = (data.records.page || []).filter((item) => {
            if (!matchesDomain(item.url, DOMAIN_FILTER)) return false;
            if (item.url.includes('/en-nz/')) return false;
            if (!SUGGEST_ALLOWED.some((p) => item.url.includes(p))) return false;
            return FILTER_URLS.length === 0 || FILTER_URLS[filterMode]((f) => item.url.includes(f));
          });

          if (!items.length) {
            hideSuggestions();
            return;
          }

          const hasResults = container.querySelector('.st-result') !== null;
          suggestions.className = hasResults ? 'results-active' : '';

          suggestions.innerHTML = items
            .map(
              (item) => `
          <a href="${escapeHtml(safeUrl(item.url))}" class="st-suggestion">
            ${escapeHtml(item.title)}
          </a>
        `
            )
            .join('');

          suggestions.style.display = 'block';
        })
        .catch(() => hideSuggestions());
    }

    function search(query) {
      if (!query) return;
      clearTimeout(suggestTimer);
      hideSuggestions();
      currentQuery = query;
      currentPage = 1;

      container.innerHTML = `
      <div class="v-stack gap-4 sm:gap-6">
        ${Array.from(
          { length: 4 },
          () => `
          <div class="h-stack align-center gap-5 sm:gap-6">
            <span class="skeleton skeleton--thumbnail"></span>
            <div class="v-stack gap-3 w-full">
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 20%"></span>
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 80%"></span>
              <span class="skeleton skeleton--text" style="--skeleton-text-width: 40%"></span>
            </div>
          </div>
        `
        ).join('')}
      </div>
    `;

      fetch(
        `https://api.swiftype.com/api/v1/public/engines/search.json?engine_key=${ENGN}&q=${encodeURIComponent(
          query
        )}&per_page=100${DOMAIN_FILTER_PARAM}`
      )
        .then((res) => res.json())
        .then((data) => {
          allResults = (data.records.page || []).filter((item) => {
            if (!matchesDomain(item.url, DOMAIN_FILTER)) return false;
            if (item.url.includes('/en-nz/')) return false;
            return FILTER_URLS.length === 0 || FILTER_URLS[filterMode]((f) => item.url.includes(f));
          });

          categories = categorizeResults(allResults);
          currentCategory = 'All';
          renderResults();
          if (closeBtn) closeBtn.style.top = '-1%';
        })
        .catch((err) => {
          console.log(err);
          container.innerHTML = '<p>Something went wrong, please try again.</p>';
        });
    }

    function submitQuery(query) {
      if (EXTERNAL_URL) {
        const target = new URL(EXTERNAL_URL, window.location.href);
        target.searchParams.set('q', query);
        window.location.href = target.href;
        return;
      }
      search(query);
    }

    searchBtn.addEventListener('click', () => {
      clearTimeout(suggestTimer);
      const query = input.value.trim();
      if (query) submitQuery(query);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        clearTimeout(suggestTimer);
        const query = input.value.trim();
        if (query) submitQuery(query);
      }
    });

    input.addEventListener('input', function () {
      clearTimeout(suggestTimer);
      const query = input.value.trim();

      if (!query) {
        hideSuggestions();
        container.innerHTML = '';
        container.style.minHeight = '0';
        if (closeBtn) closeBtn.style.top = '-16%';
        return;
      }

      suggestTimer = setTimeout(() => {
        showSuggestions(query);
      }, 400);
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) {
        hideSuggestions();
      }
    });

    container.addEventListener('click', function (e) {
      if (e.target.matches('.st-tab')) {
        currentCategory = e.target.dataset.category;
        currentPage = 1;
        renderResults();
      }
      if (e.target.matches('.st-pagination button')) {
        goToPage(parseInt(e.target.dataset.page, 10));
      }
    });

    // Run a query passed via ?q= (from an external-results instance) on load.
    // External-mode instances redirect instead of rendering, and the drawer
    // would only duplicate the page widget's fetch, so both are skipped.
    const initialQuery = (new URLSearchParams(window.location.search).get('q') || '').trim();
    if (initialQuery && !EXTERNAL_URL) {
      if (!root.closest('#search-drawer')) {
        input.value = initialQuery;
        search(initialQuery);
      }
    }
  }

  function initAll() {
    document.querySelectorAll('.st-search').forEach((root) => {
      if (root.dataset.stInit) return;
      root.dataset.stInit = 'true';
      initSearch(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
